import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, Input, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import { CtFilterWindowComponent } from '../ct-filter-window/ct-filter-window.component';
import { ADN_PLUS } from '../../models/ADN_PLUS';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { InputBaseComponent } from '../input-base.component';
import { CtInputTextComponent } from '../ct-input-text/ct-input-text.component';
import { CtCustomSelect } from '../ct-custom-select/ct-custom-select.component';
import { PfComponents } from '../../enums/pfComponents';
import { EntityField } from '../../models';
import { breakpoints } from '../../environments/environment.breakpoints';

@Component({
  selector: 'ct-filter-container',
  templateUrl: './ct-filter-container.component.html',
  styleUrls: ['./ct-filter-container.component.css'],
  host: {
    '[id]': 'getId()',
    '[hidden]': 'getHidden()'
  }
})
export class CtFilterContainerComponent extends CtrlBaseComponent implements AfterViewInit, OnInit, AfterViewChecked {

  @ViewChild('filterWindowContainer', { read: ViewContainerRef }) filterWindowTarget : ViewContainerRef;
  @ViewChild('filterTextContainer', { read: ViewContainerRef }) filterTextTarget : ViewContainerRef;
  @ViewChild('filterSelectContainer', { read: ViewContainerRef }) filterSelectTaget : ViewContainerRef;
  public filterWindowComponent : CtFilterWindowComponent;
  public filterTextComponents : Set<InputBaseComponent> = new Set<InputBaseComponent>();
  public filterSelectComponents : Set<CtCustomSelect> = new Set<CtCustomSelect>();;
  public selectedAdns : ADN_PLUS[] = [];
  private filterWindowMetaData : any[] = [];
  private filterTextMetaData : any[] = [];
  private filterSelectMetaData : any[] = [];
  private textFilterCurrentComponents : InputBaseComponent[] = []; 
  public currentTextFilters : string[] = [];
  public isMediumSize : boolean = false;
  public isSmallSize : boolean = false;
  public isLargeSize : boolean = false;
  public isFilterSelectAvailable : boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.manageWindowResizing(window.innerWidth);
  }

  private manageWindowResizing(width: number) {
    this.isMediumSize = width < breakpoints.L && width >= breakpoints.M;
    this.isSmallSize = width >= breakpoints.S && width < breakpoints.M;
    this.isLargeSize = width >= breakpoints.L;
  }

  constructor(private cdr : ChangeDetectorRef) {
    super();
  }

  /**
   * gets the id of the listened component
   */
  ngAfterViewChecked(): void {
    this.setListenedChildren();
    this.cdr.detectChanges();
  }

  /**
   * prepares the target components to be displayed
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.prepareTargetsToDisplay();
    this.manageWindowResizing(window.innerWidth);
  }

  /**
   * Paint the children components
   */
  ngAfterViewInit(): void {
    this.paintChildren( {models: this.filterWindowMetaData}, this.filterWindowTarget);
    this.paintChildren( {models: this.filterTextMetaData}, this.filterTextTarget);
    this.paintChildren( {models: this.filterSelectMetaData}, this.filterSelectTaget);
  }

  /**
   * prepares the data before it searchs
   * @param {ADN_PLUS[]} filteredAdns - the filtered ADN_PLUS[] array that is returned from the filter
   * window
   */
  public setAdnsFromFilterWindowChild(filteredAdns : ADN_PLUS[]): void {
    this.selectedAdns = filteredAdns;
    this.search();
  }

  /**
   * perfoms the search
   */
  public search() : void {
    let action = this.getEvent('onSearch');
    if(action){
      this.executeAction2(action);
    }
    this.returnSelectedAdnsToLastState();
  }

  /**
   * If the selectedAdns array is empty, return false. Otherwise, loop through each selectedAdn and loop
   * through each entity in the selectedAdn's ArrayEntities.List array. If any of the entities have a
   * Value of 'S', return true
   * @returns A boolean value.
   */
  public areSelectedAdnsChecked(): boolean {
    let result : boolean = false;
    if(this.selectedAdns.length === 0) return result;
    this.selectedAdns.forEach((selectedAdn : ADN_PLUS) => {
      selectedAdn.ArrayEntities.List.forEach((entity : EntityField) => {
        if(entity.Value !== 'N' && entity.Value !== null) result = true;
      })
    })
    return result;
  }

  /**
   * This function is called when a user clicks on a filter to clean it. It takes the filter
   * that was clicked and sets its value to 'N' (for 'No')
   * @param individualEntity - This is the entity that was selected in the filter window.
   * @param adnListIndex - The index of the ADN in the selectedAdns array.
   */
  public cleanSingleFilter(individualEntity : any, adnListIndex : any): void {
    this.selectedAdns[adnListIndex].ArrayEntities.List.forEach(( entity : EntityField ) => {
      if(JSON.stringify(entity) === JSON.stringify(individualEntity)){
        entity.Value = 'N';
        this.filterWindowComponent.recieveSelectedAdnsFromParent(this.selectedAdns, entity);
      }
    })
  }

  /**
   * Cleans the selectedAdns array and passes it to the filterWindowComponent
   */
  public cleanAllFilters() : void {
    this.selectedAdns = [];
    this.filterWindowComponent.recieveSelectedAdnsFromParent([],null);
  }

  /**
   * using the models that came from the metadata, inserts them in their target
   */
  private prepareTargetsToDisplay() {
    this.filterWindowMetaData = this.getMetaData().models.filter(( model : any ) => model.ctType === 'CtFilterWindow');
    this.filterTextMetaData = this.getMetaData().models.filter(( model : any ) => {
      if(model.ctType === PfComponents.CtInputText){
        model.controlProperties['isFilterInput'] = true;
        return model;
      }
    });
    this.filterSelectMetaData = this.getMetaData().models.filter(( model : any ) => {
      if(model.ctType === PfComponents.CtCustomSelect){
        return model;
      }
    })
  }

  /**
   * After searching, it loops through the selected ADNs, then loops through the entities in each ADN, and if the entity's
   * value is null, it sets it to 'N'
   */
  private returnSelectedAdnsToLastState(): void {
    this.selectedAdns.forEach((adn : ADN_PLUS) => {
      adn.ArrayEntities.List.forEach((entity : EntityField) => {
        if(entity.Value === null) entity.Value = 'N';
      })
    })
  }

  /**
   * It takes the string of comma separated component ids and splits it into an array of component ids.
   * Then it loops through the array and checks if the component id contains the string that is the CtType of the available components.
   * If it does, it assigns the component reference to the appropriate variable
   */
  public setListenedChildren(): void {
    let listenedComponents : string[] = this.getListenedComponentId().split(',');
    listenedComponents.forEach(( listenedComponentId : any) => {
      let listenedComponent : CtrlBaseComponent = this.utilService.getComponentRef(listenedComponentId);
      if(listenedComponent != null){        
        if(listenedComponent.getCtType().toUpperCase().includes(PfComponents.CtFilterWindow.toUpperCase())){
          this.filterWindowComponent = this.utilService.getComponentRef(listenedComponentId);
        } 
        else if(listenedComponent.getCtType().toUpperCase().includes(PfComponents.CtInputText.toUpperCase())){
          this.filterTextComponents.add(this.utilService.getComponentRef(listenedComponentId));
        }
        else if(listenedComponent.getCtType().toUpperCase().includes(PfComponents.CtCustomSelect.toUpperCase())){
          this.filterSelectComponents.add(this.utilService.getComponentRef(listenedComponentId));
        }
      }
    })
    if(this.filterSelectMetaData.length > 0) this.isFilterSelectAvailable = true;
  }

  /**
   * It receives from the textInput the input itself, then it converts the map that was filled at the creation of the component
   * to an array, search on the array the input sent by the textInput and then insert or delete the component from the textFilterCurrentComponents
   * after that it sets the contexts and perfom the search event.
   */
  public receiveTextFromChild(inputComponent : InputBaseComponent): void {
    let foundInput : number = -1;
    this.textFilterCurrentComponents = [];
    this.textFilterCurrentComponents = Array.from(this.filterTextComponents.values());

    this.textFilterCurrentComponents.forEach(( currentFilter : InputBaseComponent, index : number ) => {
      if(currentFilter.getId() === inputComponent.getId()){
        foundInput = index;
      }
    });
 
    if(foundInput !== -1){
      if(!inputComponent.getValue()){
        this.textFilterCurrentComponents.splice(foundInput,1);
      } else {
        this.textFilterCurrentComponents[foundInput].setValue(inputComponent.getValue());
      }
    }
    this.setTextValuesToContext();
    this.search();
  }

  /**
   * sets the values to the context and updates the input state
   */
  private setTextValuesToContext(): void {
    this.currentTextFilters = [];
    this.textFilterCurrentComponents.forEach(( component : InputBaseComponent ) => {
      if(component.getOldValue()){
        this.currentTextFilters.push(component.getOldValue());
      }
      this.updateInputState(component.getId(), component.getOldValue());
    })
  }

  /**
   * For every component it will update the state of the input to show/hide the search-icon
  */
  private updateInputState(componentId : string, currentValue : string): void {
    let component : CtInputTextComponent = this.utilService.getComponentRef(componentId);
    if(currentValue){
      component.setIsActive(true);
      return;
    }
    component.setIsActive(false);
  }

/**
 * Executes the search and also set as selected all the filter select components
 */
  public receiveDataFromFilterSelect(filterSelect : CtCustomSelect): void {
    filterSelect.onSelectOptionClick({
      text : filterSelect.selectedAdn.ArrayEntities.List[0]['Name'],
      value : filterSelect.selectedAdn.ArrayEntities.List[0]['Value']
    }, false);
    this.search();
  }

  //GETTERS
  getSelectedAdns(): ADN_PLUS[] { return this.selectedAdns }

  //SETTERS
  setSelectedAdns(selectedAdns: ADN_PLUS[]): void { this.selectedAdns = selectedAdns }

}